import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { useToast } from '../../hooks/useToast';

// Column definitions for each discipline with display labels
// Note: Total Field is calculated from steps, not stored
const DISCIPLINE_CONFIG = {
  piping: {
    title: 'Piping Rules of Credit',
    color: '#4a90a4',
    stepCount: 5,
    columns: [
      { key: 'transport_to_installation_area', label: 'Transport to Installation Area %' },
      { key: 'erect_supports_hangers', label: 'Erect Supports / Hangers %' },
      { key: 'fit_up', label: 'Fit Up %' },
      { key: 'bolt_weld', label: 'Bolt / Weld %' },
      { key: 'line_cleanup_hydro_test', label: 'Line Cleanup / Hydro Test %' }
    ]
  },
  mechanical: {
    title: 'Mechanical (Rotating Equipment) Rules of Credit',
    color: '#2E7D32',
    stepCount: 7,
    columns: [
      { key: 'equipment_receipt', label: 'Equipment Receipt %' },
      { key: 'measure_anchor', label: 'Measure/Anchor/Sole Plate %' },
      { key: 'rough_set', label: 'Rough Set %' },
      { key: 'pre_alignment', label: 'Pre-Alignment %' },
      { key: 'align_level', label: 'Final Align/Level %' },
      { key: 'grout_stage', label: 'Grout Stage 1 & 2 %' },
      { key: 'punchlist', label: 'Punchlist/Commissioning %' }
    ]
  },
  structural: {
    title: 'Structural Rules of Credit',
    color: '#6b5b4f',
    stepCount: 5,
    columns: [
      { key: 'receive', label: 'Receive %' },
      { key: 'layout', label: 'Layout %' },
      { key: 'erect', label: 'Erect %' },
      { key: 'bolt_up', label: 'Bolt Up %' },
      { key: 'paint', label: 'Paint %' }
    ]
  },
  hvac: {
    title: 'HVAC Rules of Credit',
    color: '#17a2b8',
    stepCount: 5,
    columns: [
      { key: 'delivery', label: 'Delivery %' },
      { key: 'rough_install', label: 'Rough Install %' },
      { key: 'supports', label: 'Supports %' },
      { key: 'accessories', label: 'Accessories %' },
      { key: 'air_balancing', label: 'Air Balancing %' }
    ]
  },
  ducts: {
    title: 'Ducts Rules of Credit',
    color: '#28a745',
    stepCount: 6,
    columns: [
      { key: 'delivery', label: 'Delivery %' },
      { key: 'rough_set', label: 'Rough Set %' },
      { key: 'supports', label: 'Supports %' },
      { key: 'bolting', label: 'Bolting %' },
      { key: 'accessories', label: 'Accessories %' },
      { key: 'grout', label: 'Grout %' }
    ]
  },
  boilermaker: {
    title: 'Boilermaker Rules of Credit',
    color: '#ef6c00',
    stepCount: 6,
    columns: [
      { key: 'delivery', label: 'Delivery %' },
      { key: 'rough_set', label: 'Rough Set %' },
      { key: 'supports', label: 'Supports %' },
      { key: 'bolting', label: 'Bolting %' },
      { key: 'accessories', label: 'Accessories %' },
      { key: 'grout', label: 'Grout %' }
    ]
  },
  vessels: {
    title: 'Vessels Rules of Credit',
    color: '#6f42c1',
    stepCount: 6,
    columns: [
      { key: 'equipment_receipt', label: 'Equipment Receipt %' },
      { key: 'measure_anchor', label: 'Measure/Anchor/Bush Hammer %' },
      { key: 'rough_set', label: 'Rough Set %' },
      { key: 'grout', label: 'Grout %' },
      { key: 'vessel_close_out', label: 'Vessel Close Out %' },
      { key: 'insulation', label: 'Insulation %' }
    ]
  },
  skids: {
    title: 'Skids Rules of Credit',
    color: '#795548',
    stepCount: 5,
    columns: [
      { key: 'received', label: 'Received %' },
      { key: 'layout', label: 'Layout %' },
      { key: 'grout', label: 'Grout %' },
      { key: 'rough_set', label: 'Rough Set %' },
      { key: 'final_set', label: 'Final Set %' }
    ]
  },
  chutes: {
    title: 'Chutes Rules of Credit',
    color: '#e67e22',
    stepCount: 5,
    columns: [
      { key: 'receive', label: 'Receive %' },
      { key: 'install_supports', label: 'Install Supports %' },
      { key: 'field_trim', label: 'Field Trim %' },
      { key: 'weld_bolt_up', label: 'Weld / Bolt Up %' },
      { key: 'punch_list', label: 'Punch List %' }
    ]
  },
  conveyors: {
    title: 'Conveyors Rules of Credit',
    color: '#d35400',
    stepCount: 8,
    columns: [
      { key: 'equipment_receipt', label: 'Equipment Receipt %' },
      { key: 'layout_anchor', label: 'Layout / Anchor %' },
      { key: 'prep_concrete', label: 'Prepare Concrete Base %' },
      { key: 'rough_set', label: 'Rough Set %' },
      { key: 'align_level', label: 'Align & Level %' },
      { key: 'torque_bolts', label: 'Torque Bolts %' },
      { key: 'grout_stage', label: 'Grout Stage %' },
      { key: 'punchlist', label: 'Punchlist / Commissioning %' }
    ]
  },
  civil: {
    title: 'Civil Rules of Credit',
    color: '#8d6e63',
    stepCount: 6,
    columns: [
      { key: 'site_prep', label: 'Site Prep %' },
      { key: 'excavation', label: 'Excavation %' },
      { key: 'formwork', label: 'Formwork %' },
      { key: 'rebar', label: 'Rebar %' },
      { key: 'pour_concrete', label: 'Pour Concrete %' },
      { key: 'backfill', label: 'Backfill %' }
    ]
  },
  electrical: {
    title: 'Electrical Rules of Credit',
    color: '#f1c40f',
    stepCount: 5,
    columns: [
      { key: 'conduit_install', label: 'Conduit Install %' },
      { key: 'cable_pull', label: 'Cable Pull %' },
      { key: 'termination', label: 'Termination %' },
      { key: 'testing', label: 'Testing %' },
      { key: 'energize', label: 'Energize %' }
    ]
  }
};

const RulesOfCreditModal = React.memo(({ isOpen, onClose, discipline, jobNumber, onSave }) => {
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [data, setData] = useState({});
  const [error, setError] = useState(null);
  const { toast, showToast } = useToast();
  const [confirmApply, setConfirmApply] = useState({ show: false, affectedRows: 0, savedData: null });

  // Freeze + "Received by LGL" toggle state
  const [frozen, setFrozen] = useState(false);
  const [frozenAt, setFrozenAt] = useState(null);
  const [frozenBy, setFrozenBy] = useState(null);
  const [lglReceives, setLglReceives] = useState(false);
  const [canFreeze, setCanFreeze] = useState(false);
  const [isSuperadmin, setIsSuperadmin] = useState(false);
  const [presets, setPresets] = useState(null);
  const [freezing, setFreezing] = useState(false);
  const [confirmFreeze, setConfirmFreeze] = useState(false);

  const config = DISCIPLINE_CONFIG[discipline];

  useEffect(() => {
    if (isOpen && discipline && jobNumber) {
      fetchData();
    }
  }, [isOpen, discipline, jobNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authenticatedApiCall(instance, accounts, 'GET', `/rules-of-credit/${discipline}/${jobNumber}`);
      if (response.data.success) {
        const d = response.data.data || {};
        setData(d);
        setFrozen(d.is_frozen === true);
        setFrozenAt(d.frozen_at || null);
        setFrozenBy(d.frozen_by || null);
        setLglReceives(d.lgl_receives === true);
        setPresets(response.data.presets || null);
        setCanFreeze(response.data.canFreeze === true);
        setIsSuperadmin(response.data.isSuperadmin === true);
      } else {
        throw new Error(response.data.message);
      }
    } catch (err) {
      setError('Failed to load rules of credit');
      console.error('Error fetching rules of credit:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    if (frozen) return; // locked
    // Allow empty string or partial decimals while typing (e.g., "0.", ".1", "15.")
    // This prevents the input from jumping around while user types
    if (value === '' || value === '.' || /^\d*\.?\d*$/.test(value)) {
      setData(prev => ({ ...prev, [key]: value }));
    }
  };

  const handleBlur = (key) => {
    if (frozen) return;
    // On blur, parse and clamp the value
    const rawValue = data[key];
    const numValue = parseFloat(rawValue) || 0;
    const clampedValue = Math.min(100, Math.max(0, numValue));
    setData(prev => ({ ...prev, [key]: clampedValue }));
  };

  // "Received by LGL" toggle: loads the matching established preset into the inputs.
  const handleToggleLglReceives = (value) => {
    if (frozen || !presets) return;
    setLglReceives(value);
    const preset = value ? presets.are_responsible : presets.not_responsible;
    if (preset) {
      setData(prev => ({ ...prev, ...preset }));
    }
  };

  const handleFreeze = async () => {
    try {
      setFreezing(true);
      const payload = { ...data, lgl_receives: lglReceives };
      const response = await authenticatedApiCall(instance, accounts, 'POST', `/rules-of-credit/${discipline}/${jobNumber}/freeze`, payload);
      if (response.data.success) {
        showToast(`Rules of Credit frozen. Applied to ${(response.data.updatedRows || 0).toLocaleString()} row(s).`, 'success');
        setFrozen(true);
        setConfirmFreeze(false);
        setTimeout(() => {
          if (onSave) onSave(response.data.data);
          onClose();
        }, 1500);
      } else {
        throw new Error(response.data.message);
      }
    } catch (err) {
      showToast(`Freeze failed: ${err.response?.data?.message || err.message}`, 'error');
    } finally {
      setFreezing(false);
    }
  };

  const handleUnfreeze = async () => {
    try {
      setFreezing(true);
      const response = await authenticatedApiCall(instance, accounts, 'POST', `/rules-of-credit/${discipline}/${jobNumber}/unfreeze`);
      if (response.data.success) {
        showToast('Rules of Credit unfrozen. They can now be edited.', 'success');
        setFrozen(false);
        setFrozenBy(null);
        setFrozenAt(null);
      } else {
        throw new Error(response.data.message);
      }
    } catch (err) {
      showToast(`Unfreeze failed: ${err.response?.data?.message || err.message}`, 'error');
    } finally {
      setFreezing(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await authenticatedApiCall(instance, accounts, 'PUT', `/rules-of-credit/${discipline}/${jobNumber}`, data);
      if (response.data.success) {
        const { affectedRows } = response.data;
        
        // If there are existing rows, ask if the user wants to apply to them (all disciplines)
        if (affectedRows > 0) {
          showToast('Rules saved!', 'success');
          setConfirmApply({ show: true, affectedRows, savedData: response.data.data });
        } else {
          showToast('Rules saved successfully!', 'success');
          setTimeout(() => {
            if (onSave) onSave(response.data.data);
            onClose();
          }, 1500);
        }
      } else {
        throw new Error(response.data.message);
      }
    } catch (err) {
      showToast(`Save failed: ${err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleApplyToExisting = async () => {
    try {
      setApplying(true);
      const response = await authenticatedApiCall(instance, accounts, 'PUT', `/rules-of-credit/${discipline}/${jobNumber}/apply`);
      if (response.data.success) {
        showToast(`Applied rules to ${response.data.updatedRows.toLocaleString()} rows!`, 'success');
        setTimeout(() => {
          setConfirmApply({ show: false, affectedRows: 0, savedData: null });
          if (onSave) onSave(confirmApply.savedData);
          onClose();
        }, 2000);
      } else {
        throw new Error(response.data.message);
      }
    } catch (err) {
      showToast(`Apply failed: ${err.message}`, 'error');
    } finally {
      setApplying(false);
    }
  };

  const handleSkipApply = () => {
    setConfirmApply({ show: false, affectedRows: 0, savedData: null });
    if (onSave) onSave(confirmApply.savedData);
    onClose();
  };

  const calculateTotal = () => {
    if (!config) return 0;
    return config.columns.reduce((sum, col) => {
      const val = data[col.key];
      return sum + (typeof val === 'string' ? parseFloat(val) || 0 : val || 0);
    }, 0);
  };

  if (!isOpen || !config) return null;

  const totalIsValid = Math.abs(calculateTotal() - 100) < 0.1;

  // Confirmation dialog for freezing the Rules of Credit for the whole project
  if (confirmFreeze) {
    return (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
        {toast.show && (
          <div style={{ position: 'fixed', top: '20px', right: '20px', padding: '1rem 1.5rem', backgroundColor: toast.type === 'success' ? '#28a745' : '#dc3545', color: 'white', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 9999 }}>
            {toast.message}
          </div>
        )}
        <div style={{ backgroundColor: 'white', borderRadius: '8px', width: '90%', maxWidth: '520px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)' }}>
          <div style={{ backgroundColor: '#0d6efd', color: 'white', padding: '1.5rem' }}>
            <h3 style={{ margin: 0 }}>🔒 Freeze Rules of Credit?</h3>
          </div>
          <div style={{ padding: '1.5rem' }}>
            <p style={{ margin: '0 0 1rem 0', fontSize: '1rem', lineHeight: '1.6' }}>
              You are about to <strong>freeze</strong> the {config.title.replace(' Rules of Credit', '')} Rules of Credit for job <strong>{jobNumber}</strong>
              {presets ? (<> using the <strong>{lglReceives ? 'Received by LGL' : 'Not Received by LGL'}</strong> preset</>) : null}.
            </p>
            <div style={{ padding: '1rem', backgroundColor: '#fff3cd', borderRadius: '4px', marginBottom: '1rem', fontSize: '0.9rem', color: '#856404' }}>
              Once frozen, the percentages are <strong>locked for the life of the project</strong> and progress entry is unlocked for this discipline. Only a superadmin can unfreeze them.
            </div>
            {!totalIsValid && (
              <div style={{ color: '#dc3545', fontSize: '0.9rem' }}>⚠️ Total must equal 100% before freezing (currently {calculateTotal().toFixed(1)}%).</div>
            )}
          </div>
          <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #dee2e6', display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
            <button onClick={() => setConfirmFreeze(false)} disabled={freezing} style={{ padding: '0.75rem 1.5rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', opacity: freezing ? 0.5 : 1 }}>Cancel</button>
            <button onClick={handleFreeze} disabled={freezing || !totalIsValid} style={{ padding: '0.75rem 1.5rem', backgroundColor: '#0d6efd', color: 'white', border: 'none', borderRadius: '4px', cursor: (freezing || !totalIsValid) ? 'not-allowed' : 'pointer', opacity: (freezing || !totalIsValid) ? 0.5 : 1 }}>
              {freezing ? 'Freezing...' : '🔒 Freeze for Project'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Confirmation dialog for applying to existing rows
  if (confirmApply.show) {
    return (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
        {toast.show && (
          <div style={{ position: 'fixed', top: '20px', right: '20px', padding: '1rem 1.5rem', backgroundColor: toast.type === 'success' ? '#28a745' : '#dc3545', color: 'white', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 9999 }}>
            {toast.message}
          </div>
        )}
        <div style={{ backgroundColor: 'white', borderRadius: '8px', width: '90%', maxWidth: '500px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)' }}>
          {/* Header */}
          <div style={{ backgroundColor: '#17a2b8', color: 'white', padding: '1.5rem' }}>
            <h3 style={{ margin: 0 }}>Apply to Existing Rows?</h3>
          </div>
          
          {/* Content */}
          <div style={{ padding: '1.5rem' }}>
            <p style={{ margin: '0 0 1rem 0', fontSize: '1rem', lineHeight: '1.6' }}>
              Rules saved successfully! There are <strong>{confirmApply.affectedRows.toLocaleString()}</strong> existing 
              rows for job <strong>{jobNumber}</strong>.
            </p>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.95rem', color: '#666' }}>
              Would you like to apply these new rules to all existing rows? This will recalculate 
              their Total Field Completion based on the new weights.
            </p>
            <div style={{ padding: '1rem', backgroundColor: '#fff3cd', borderRadius: '4px', marginBottom: '1rem' }}>
              <strong>Note:</strong> New rows added in the future will automatically use these rules.
            </div>
          </div>
          
          {/* Footer */}
          <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #dee2e6', display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
            <button 
              onClick={handleSkipApply} 
              disabled={applying}
              style={{ padding: '0.75rem 1.5rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', opacity: applying ? 0.5 : 1 }}
            >
              Skip (Only New Rows)
            </button>
            <button 
              onClick={handleApplyToExisting} 
              disabled={applying}
              style={{ padding: '0.75rem 1.5rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: applying ? 0.5 : 1 }}
            >
              {applying ? 'Applying...' : `Apply to ${confirmApply.affectedRows.toLocaleString()} Rows`}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
      {toast.show && (
        <div style={{ position: 'fixed', top: '20px', right: '20px', padding: '1rem 1.5rem', backgroundColor: toast.type === 'success' ? '#28a745' : '#dc3545', color: 'white', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 9999 }}>
          {toast.message}
        </div>
      )}
      
      <div style={{ backgroundColor: 'white', borderRadius: '8px', width: '90%', maxWidth: '600px', maxHeight: '80vh', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)' }}>
        {/* Header */}
        <div style={{ backgroundColor: config.color, color: 'white', padding: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0 }}>📊 {config.title}</h3>
            <span style={{ fontSize: '0.85rem', opacity: 0.9 }}>Job: {jobNumber}</span>
          </div>
          <button onClick={onClose} disabled={saving} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: 'white', opacity: saving ? 0.5 : 1 }}>×</button>
        </div>

        {/* Content */}
        <div style={{ padding: '1.5rem', maxHeight: 'calc(80vh - 150px)', overflow: 'auto' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div style={{ display: 'inline-block', width: '40px', height: '40px', border: '4px solid #f3f3f3', borderTop: `4px solid ${config.color}`, borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
              <p style={{ marginTop: '1rem', color: '#666' }}>Loading rules...</p>
              <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            </div>
          ) : error ? (
            <div style={{ padding: '1rem', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', color: '#856404' }}>
              {error}
              <button onClick={fetchData} style={{ marginLeft: '1rem', padding: '0.25rem 0.5rem', border: 'none', borderRadius: '4px', backgroundColor: config.color, color: 'white', cursor: 'pointer' }}>Retry</button>
            </div>
          ) : (
            <div>
              {frozen && (
                <div style={{ padding: '0.75rem 1rem', backgroundColor: '#e7f1ff', border: '1px solid #0d6efd', borderRadius: '6px', marginBottom: '1rem', color: '#084298', fontSize: '0.9rem' }}>
                  🔒 <strong>Frozen</strong>{frozenBy ? ` by ${frozenBy}` : ''}{frozenAt ? ` on ${new Date(frozenAt).toLocaleDateString()}` : ''}. Rules are locked for the life of this project.
                  {isSuperadmin ? ' A superadmin can unfreeze below to make corrections.' : ''}
                </div>
              )}

              {presets && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', padding: '0.75rem 1rem', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '6px', marginBottom: '1rem' }}>
                  <div>
                    <div style={{ fontWeight: 600, color: '#333' }}>Received by LGL</div>
                    <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>Switch between the established presets, review, then freeze.</div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={() => handleToggleLglReceives(false)}
                      disabled={frozen}
                      style={{ padding: '0.5rem 0.9rem', borderRadius: '4px', border: `2px solid ${!lglReceives ? config.color : '#dee2e6'}`, backgroundColor: !lglReceives ? config.color : 'white', color: !lglReceives ? 'white' : '#333', cursor: frozen ? 'not-allowed' : 'pointer', fontSize: '0.85rem', opacity: frozen ? 0.6 : 1 }}
                    >
                      Not Received
                    </button>
                    <button
                      onClick={() => handleToggleLglReceives(true)}
                      disabled={frozen}
                      style={{ padding: '0.5rem 0.9rem', borderRadius: '4px', border: `2px solid ${lglReceives ? config.color : '#dee2e6'}`, backgroundColor: lglReceives ? config.color : 'white', color: lglReceives ? 'white' : '#333', cursor: frozen ? 'not-allowed' : 'pointer', fontSize: '0.85rem', opacity: frozen ? 0.6 : 1 }}
                    >
                      LGL Receives
                    </button>
                  </div>
                </div>
              )}

              <p style={{ margin: '0 0 1rem 0', color: '#666', fontSize: '0.9rem' }}>
                Set the percentage credit for each of the {config.stepCount} steps. Total Field should equal 100%.
              </p>
              
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                {config.columns.map((col, index) => (
                  <div key={col.key} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <span style={{ width: '24px', textAlign: 'center', color: '#6c757d', fontSize: '0.85rem' }}>{index + 1}.</span>
                    <label style={{ flex: 1, color: '#333' }}>
                      {col.label}
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={data[col.key] ?? ''}
                        onChange={(e) => handleChange(col.key, e.target.value)}
                        onBlur={() => handleBlur(col.key)}
                        placeholder="0"
                        disabled={frozen}
                        style={{
                          width: '80px',
                          padding: '8px 12px',
                          border: '2px solid #dee2e6',
                          borderRadius: '4px',
                          fontSize: '1rem',
                          textAlign: 'right',
                          backgroundColor: frozen ? '#f1f3f5' : 'white',
                          color: frozen ? '#868e96' : 'inherit',
                          cursor: frozen ? 'not-allowed' : 'text'
                        }}
                      />
                      <span style={{ color: '#6c757d' }}>%</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Total Field Calculation */}
              <div style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: config.color + '15', borderRadius: '8px', border: `2px solid ${config.color}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: '600', color: config.color }}>📊 Total Field ({config.stepCount} steps):</span>
                  <span style={{ 
                    fontSize: '1.5rem', 
                    fontWeight: '700', 
                    color: Math.abs(calculateTotal() - 100) < 0.1 ? '#28a745' : '#dc3545'
                  }}>
                    {calculateTotal().toFixed(1)}%
                  </span>
                </div>
                {Math.abs(calculateTotal() - 100) >= 0.1 && (
                  <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#dc3545' }}>
                    ⚠️ Total should equal 100% ({calculateTotal() < 100 ? 'Under' : 'Over'} by {Math.abs(calculateTotal() - 100).toFixed(1)}%)
                  </div>
                )}
                {Math.abs(calculateTotal() - 100) < 0.1 && (
                  <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#28a745' }}>
                    ✓ Total equals 100%
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid #dee2e6', display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
          <button onClick={onClose} disabled={saving || freezing} style={{ padding: '0.75rem 1.5rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', opacity: (saving || freezing) ? 0.5 : 1 }}>Close</button>

          {frozen ? (
            isSuperadmin && (
              <button onClick={handleUnfreeze} disabled={freezing || loading} style={{ padding: '0.75rem 1.5rem', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: (freezing || loading) ? 0.5 : 1 }}>
                {freezing ? 'Unfreezing...' : '🔓 Unfreeze (Superadmin)'}
              </button>
            )
          ) : (
            <>
              <button onClick={handleSave} disabled={saving || loading} style={{ padding: '0.75rem 1.5rem', backgroundColor: config.color, color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: (saving || loading) ? 0.5 : 1 }}>
                {saving ? 'Saving...' : '💾 Save Rules'}
              </button>
              {canFreeze && (
                <button onClick={() => setConfirmFreeze(true)} disabled={saving || loading} title={totalIsValid ? 'Freeze Rules of Credit for the life of the project' : 'Total must equal 100% to freeze'} style={{ padding: '0.75rem 1.5rem', backgroundColor: '#0d6efd', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: (saving || loading) ? 0.5 : 1 }}>
                  🔒 Freeze for Project
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
});

export default RulesOfCreditModal;

